import { CommonSearchComponent, DashboardAsideComponent, DashboardTableComponent, Tour } from "@components";
import {
    AppState,
    CampaignInfo,
    campaignSpentBudgetSelector,
    changeDashboardLinkAction,
    CurrencyMapping,
    getCampaignByIdAction,
    removeCapmaignByIdAction,
    selectCampaignAction,
    isReportingOnlyCustomer
} from "@store";
import { History, Location } from "history";
import * as React from "react";
import { connect, Dispatch } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
    FetchingState,
    FETCH_STATE_ERROR,
    FETCH_STATE_FETCHING,
    FETCH_STATE_SUCCESS
} from "../../node_modules/rd-redux-http";
import { DashboardUrl, EditCampaignUrl, CampaignUrl } from "../common/urls";
import { DashboardFiltersComponent, FiltersInterface } from "../components/DashboardFilters";
import { DefaultLayout } from "../layouts";
import "./Dashboard.css";
import { CampaignSpentBudget } from "@common";

export interface DashboardComponentProps {
    history: History;
    location: Location;
    searchValue: string;
    filters: FiltersInterface;
    campaigns: CampaignInfo[];
    loadingFailed: boolean;
    loading: boolean;
    loadingCampaigns: boolean;
    selectedCampaign: CampaignInfo;
    selectedJustPurchasedCampaign: CampaignInfo;
    selectedCampaignSpentBudget: CampaignSpentBudget & { isLoading: boolean };
    currencies: CurrencyMapping[];
    /**
     * https://adello.atlassian.net/browse/ACD-594
     * To disable page for reporting-only customers
     */
    disableContent: boolean;

    clientId: string;
    selectedPage: number;
    onSearch: (query: string) => void;
    onFilter: (filters: string[]) => void;
    onSelectCampaign: (campaignId: string) => void;
    onPageChange: (page: number) => void;
    onSort: (sortUrl: string) => void;
    onDelete: (clientId: string, campaignId: string) => void;
    onLinkToCampaignEditPage: (clientId: string, campaignId: string) => void;
    onDoubleClickCampaign: (campaignId: string, clientId: string) => void;
    onLinkToNewCampaignPage: (clientId: string) => void;
}

export class DashboardComponent extends React.Component<DashboardComponentProps> {
    renderAside(): React.ReactChild {
        return (
            <DashboardAsideComponent
                selectedCampaign={this.props.selectedCampaign || this.props.selectedJustPurchasedCampaign}
                spentBudget={this.props.selectedCampaignSpentBudget}
            />
        );
    }
    render() {
        const {
            searchValue,
            onSearch,
            filters,
            onFilter,
            campaigns,
            loadingFailed,
            history,
            location,
            onSelectCampaign,
            selectedCampaign = this.props.selectedJustPurchasedCampaign,
            selectedPage,
            onPageChange,
            clientId,
            onSort,
            loading,
            loadingCampaigns,
            currencies,
            disableContent,
            onDelete,
            onLinkToCampaignEditPage,
            onDoubleClickCampaign,
            onLinkToNewCampaignPage
        } = this.props;

        return (
            <DefaultLayout
                history={history}
                location={location}
                asideChildren={() => this.renderAside()}
                selectedPage="dashboard"
                clientId={clientId}
                loading={loading}
                loadingFailed={loadingFailed}
                disableContent={disableContent}
            >
                <section className="dashboard-header">
                    <div className="title-wrapper">
                        <h2>Dashboard</h2>
                        <h6>Campaign List</h6>
                    </div>
                </section>
                <section className="dashboard-filters">
                    <DashboardFiltersComponent
                        filters={filters}
                        onFilter={onFilter}
                        onLinkToNewCampaignPage={() => onLinkToNewCampaignPage(clientId)}
                    />
                    <CommonSearchComponent
                        placeholder="Search Campaign"
                        searchValue={searchValue}
                        onSubmit={onSearch}
                    />
                </section>
                <section className="result-table tour-dashboard-campaign-list">
                    <DashboardTableComponent
                        campaigns={campaigns}
                        loadingCampaigns={loadingCampaigns}
                        onPageChange={onPageChange}
                        items={10}
                        selectedIndex={selectedPage}
                        onSelectCampaign={onSelectCampaign}
                        selectedCampaignId={selectedCampaign && selectedCampaign.campaignId}
                        onSort={onSort}
                        currencies={currencies}
                        onDelete={onDelete}
                        onLinkToCampaignEditPage={onLinkToCampaignEditPage}
                        clientId={clientId}
                        onDoubleClickCampaign={onDoubleClickCampaign}
                    />
                </section>
                <Tour tour="dashboard" isRunning={!loading} />
            </DefaultLayout>
        );
    }
}

export interface DashboardPageProps extends RouteComponentProps<{}> {}

export const DashboardPage = connect(
    (state: AppState, ownProps: DashboardPageProps): Partial<DashboardComponentProps> => {
        const match = DashboardUrl.match(ownProps.location, true);
        const matchDashboardUrl = DashboardUrl.match(ownProps.history.location.pathname, true);
        const onlyReporting = isReportingOnlyCustomer(state);

        const defaultFilters = {
            draft: false,
            pending: false,
            approved: false,
            review: false,
            running: false,
            paused: false,
            suspended: false,
            completed: false,
            archived: false
        };
        const newFilters: {} = {};
        let campaigns: any;
        let searchValue: string;
        let sortBy: string;
        let page: number = 0;
        let pageItems: number = 10;

        if (match.isMatched && match.query) {
            searchValue = match.query.search;
            page = Number(match.query.page ? match.query.page : 0);
            const filters = Object.keys(match.query).filter(el => el !== "search");
            sortBy = match.query.sortBy;

            for (let key in defaultFilters) {
                newFilters[key] = filters.some(filter => filter === key) ? true : false;
            }
        }
        searchValue = searchValue ? searchValue : "";

        if (state.campaignList.campaigns.fetchState === FETCH_STATE_SUCCESS) {
            campaigns = state.campaignList.campaigns.data;
        }
        const filters = Object.keys(newFilters).length ? newFilters : defaultFilters;

        const activeFilters: string[] = [];
        for (let filter in filters) {
            if (filters[filter] === true) activeFilters.push(filter);
        }

        if (campaigns && activeFilters.length > 0) {
            campaigns = campaigns.filter((campaign: CampaignInfo) =>
                activeFilters.some(filter => campaign.status === filter)
            );
        }

        if (campaigns && searchValue) {
            campaigns = campaigns.filter((campaign: CampaignInfo) => {
                const regExp = new RegExp(searchValue.trim().toLowerCase());
                const campaignName = campaign.campaignName && campaign.campaignName.trim().toLocaleLowerCase();

                return regExp.test(campaignName);
            });
        }

        if (campaigns && sortBy) {
            const sortByValue = sortBy.replace(/ [A-Za-z]*/, "");
            const sortType = sortBy.replace(/[A-Za-z]* /, "");

            campaigns = campaigns.sort((campaignA: CampaignInfo, campaignB: CampaignInfo) => {
                const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
                const a = String(campaignA[sortByValue]).toLocaleLowerCase();
                const b = String(campaignB[sortByValue]).toLocaleLowerCase();

                if (String(campaignA[sortByValue]).toLocaleLowerCase() !== [a, b].sort(collator.compare)[0])
                    return sortType === "asc" ? -1 : 1;
                return sortType === "asc" ? 1 : -1;
            });
        }

        if (campaigns && page - 1 > campaigns.length / pageItems) {
            // If page index is highter than pages amount,reset page index to 0
            page = 0;
        }
        const selectedCampaign: CampaignInfo =
            campaigns &&
            state.campaignList.selectedCampaignId &&
            campaigns.filter(
                (campaign: CampaignInfo) => campaign.campaignId === state.campaignList.selectedCampaignId
            )[0];

        const selectedJustPurchasedCampaign: CampaignInfo =
            campaigns &&
            state.campaignList.selectedPurchasedCampaignName &&
            campaigns.filter(
                (campaign: CampaignInfo) => campaign.campaignName === state.campaignList.selectedPurchasedCampaignName
            )[0];

        const loading: boolean =
            state.clientState.clientSavingFlag || state.campaignList.campaigns.fetchState === FETCH_STATE_FETCHING;

        const selectedCampaignSpentBudget = campaignSpentBudgetSelector(
            state,
            selectedCampaign
                ? selectedCampaign.campaignId
                : selectedJustPurchasedCampaign
                    ? selectedJustPurchasedCampaign.campaignId
                    : ""
        );

        return {
            history: ownProps.history,
            location: ownProps.history.location,
            searchValue,
            filters: filters as FiltersInterface,
            campaigns,
            selectedPage: page != 0 ? page - 1 : page,
            loadingFailed: state.campaignList.campaigns.fetchState === FETCH_STATE_ERROR ? true : false,
            loading,
            loadingCampaigns: state.campaignList.campaigns.fetchState === FETCH_STATE_FETCHING,
            selectedCampaign: selectedCampaign || undefined,
            selectedJustPurchasedCampaign: selectedJustPurchasedCampaign || undefined,
            selectedCampaignSpentBudget,
            clientId: matchDashboardUrl.isMatched && matchDashboardUrl.params.clientId,
            currencies: FetchingState.isSuccess(state.mappings.currencies) && state.mappings.currencies.data,
            disableContent: onlyReporting
        };
    },
    (dispatch: Dispatch, ownProps: DashboardPageProps) => ({
        onSearch: (query: string) => {
            const match = DashboardUrl.match(ownProps.location, true);
            if (match.isMatched) {
                const newUrl = DashboardUrl.format(match.params, {
                    ...match.query,
                    search: query
                });
                dispatch(changeDashboardLinkAction({ link: newUrl }));
                ownProps.history.push(newUrl);
            }
        },

        onFilter: (filters: string[]) => {
            const match = DashboardUrl.match(ownProps.location, true);
            if (match.isMatched) {
                const query = {
                    search: (match.query || {}).search
                };

                filters.forEach((filter: string) => {
                    query[filter] = true;
                });
                const newUrl = DashboardUrl.format(match.params, { ...query, ...{ page: 1 } });
                dispatch(changeDashboardLinkAction({ link: newUrl }));
                ownProps.history.push(newUrl);
            }
        },

        onSort: (sortUrl: string) => {
            const match = DashboardUrl.match(ownProps.location, true);
            if (match.isMatched) {
                const newUrl = DashboardUrl.format(match.params, {
                    ...match.query,
                    sortBy: sortUrl
                });
                dispatch(changeDashboardLinkAction({ link: newUrl }));
                ownProps.history.push(newUrl);
            }
        },

        onPageChange: (page: number) => {
            const match = DashboardUrl.match(ownProps.location, true);
            if (match.isMatched) {
                const query = { ...match.query, ...{ page } };
                const newUrl = DashboardUrl.format(match.params, query);
                dispatch(changeDashboardLinkAction({ link: newUrl }));
                ownProps.history.push(newUrl);
            }
        },

        onSelectCampaign: (campaignId: string) => {
            dispatch(selectCampaignAction({ campaignId: campaignId }));
        },

        onDelete: (clientId: string, campaignId: string) => {
            let state = confirm("Are you sure wan't to remove campaign?");
            if (state) {
                dispatch(removeCapmaignByIdAction({ campaignId, clientId }));
            }
        },

        onLinkToCampaignEditPage: (clientId: string, campaignId: string) => {
            ownProps.history.push(EditCampaignUrl.format({ clientId: clientId, campaignId: campaignId, mode: "edit" }));
        },

        onDoubleClickCampaign: (clientId: string, campaignId: string) => {
            ownProps.history.push(
                EditCampaignUrl.format({ clientId: clientId, campaignId: campaignId, mode: "summary" })
            );
            dispatch(getCampaignByIdAction({ clientId: clientId, campaignId: campaignId, mode: "summary" }));
        },
        onLinkToNewCampaignPage: (clientId: string) => {
            ownProps.history.push(CampaignUrl.format({ clientId: clientId }));
        }
    })
)(DashboardComponent);
